import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';
import { personalInfo, education } from '../data/portfolio';

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] } },
});
const fadeLeft = (delay = 0) => ({
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] } },
});
const fadeRight = (delay = 0) => ({
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] } },
});

export default function About() {
  const [ref, inView] = useInView(0.15);

  return (
    <section id="about" className="section" ref={ref}>
      {/* Subtle divider line */}
      <div style={{ position: 'absolute', top: 0, left: '5%', right: '5%', height: 1, background: 'linear-gradient(to right, transparent, rgba(0,245,255,0.1), transparent)' }} />

      <div className="container">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(2rem, 5vw, 5rem)', alignItems: 'center' }} className="grid-2">

          {/* Left */}
          <motion.div variants={fadeLeft(0)} initial="hidden" animate={inView ? 'visible' : 'hidden'}>
            <div className="sec-tag">01 · About Me</div>
            <h2 className="sec-title" style={{ marginBottom: '1.5rem' }}>
              The dev<br />
              <span style={{
                background: 'linear-gradient(90deg, var(--cyan), var(--teal))',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                filter: 'drop-shadow(0 0 10px rgba(0,245,255,0.3))',
              }}>
                behind the commits
              </span>
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', color: 'var(--grey)', lineHeight: 1.8, fontSize: '0.93rem' }}>
              <p>
                I'm <strong style={{ color: 'var(--white)' }}>Dharshith Dev KP</strong>, a Full Stack Developer and AI Engineer from Bangalore (originally Kannur, Kerala). I build production-ready platforms — from MERN-stack web apps to intelligent AI pipelines.
              </p>
              <p>
                At <strong style={{ color: 'var(--cyan)' }}>SN Plastics</strong>, I architect and maintain their internal software suite, optimizing MySQL databases and building automation tools that measurably reduce overhead. Software should solve real operational problems.
              </p>
              <p>
                I specialize in <strong style={{ color: 'var(--white)' }}>backend systems</strong>, <strong style={{ color: 'var(--white)' }}>RESTful API design</strong>, and <strong style={{ color: 'var(--cyan)' }}>RAG / AI applications</strong>. My approach: clean architecture, data integrity, measurable performance.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem', flexWrap: 'wrap' }}>
              <a href={`mailto:${personalInfo.email}`} className="btn-cyan" style={{ fontSize: '0.65rem', padding: '0.65rem 1.4rem' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                Email Me
              </a>
              <a href={personalInfo.github} target="_blank" rel="noopener noreferrer" className="btn-outline" style={{ fontSize: '0.65rem', padding: '0.65rem 1.4rem' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
                GitHub
              </a>
            </div>
          </motion.div>

          {/* Right cards */}
          <motion.div variants={fadeRight(0.2)} initial="hidden" animate={inView ? 'visible' : 'hidden'} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            {/* Profile glass card */}
            <div className="glass-card" style={{ padding: '1.5rem', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(to right, var(--cyan), transparent)' }} />
              <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(0,245,255,0.04)', filter: 'blur(20px)' }} />
              <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', position: 'relative' }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '14px', flexShrink: 0,
                  background: 'linear-gradient(135deg, rgba(0,245,255,0.15), rgba(0,180,216,0.1))',
                  border: '1px solid rgba(0,245,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 0 20px rgba(0,245,255,0.15)',
                }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: '1.2rem', color: 'var(--cyan)', textShadow: '0 0 15px var(--cyan)' }}>DD</span>
                </div>
                <div>
                  <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '1.1rem', color: 'var(--white)' }}>{personalInfo.name}</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.6rem', color: 'var(--cyan)', marginTop: '0.2rem', letterSpacing: '0.08em' }}>FULLSTACK · AI ENGINEER</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--grey)', marginTop: '0.35rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    {personalInfo.location}
                  </div>
                </div>
              </div>
            </div>

            {/* Education */}
            {education.map((edu, i) => (
              <div key={i} className="glass-card" style={{ padding: '1.25rem 1.5rem' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.55rem', letterSpacing: '0.2em', color: 'var(--cyan)', marginBottom: '0.5rem', opacity: 0.8 }}>EDUCATION</div>
                <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '1rem', color: 'var(--white)' }}>{edu.degree}</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--grey)', marginTop: '0.2rem' }}>{edu.institution}</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.6rem', color: 'var(--grey2)', marginTop: '0.4rem', letterSpacing: '0.05em' }}>{edu.period}</div>
              </div>
            ))}

            {/* Quick facts grid */}
            <div className="glass-card" style={{ padding: '1.25rem 1.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              {[
                { label: 'Current Role', value: 'SN Plastics' },
                { label: 'Focus Area', value: 'Backend + AI' },
                { label: 'Primary Stack', value: 'MERN + LangChain' },
                { label: 'Status', value: '🟢 Available' },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.52rem', letterSpacing: '0.1em', color: 'var(--grey2)', textTransform: 'uppercase' }}>{label}</div>
                  <div style={{ fontFamily: 'var(--font-heading)', fontSize: '0.9rem', fontWeight: 600, color: 'var(--white)', marginTop: '0.2rem' }}>{value}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
